import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import BookingItemMonth from './BookingItemMonth';

function MonthView({ resources, bookings, startDate, onDayClick }) {
  const navigate = useNavigate();
  // Get the first and last day of the month
  const monthStart = moment(startDate).startOf('month');
  const monthEnd = moment(startDate).endOf('month');
  
  // Get the start of the week containing the first day of the month
  const calendarStart = moment(monthStart).startOf('isoWeek');
  
  // Get the end of the week containing the last day of the month
  const calendarEnd = moment(monthEnd).endOf('isoWeek');
  
  // Generate all days to display in the calendar
  const days = [];
  let currentDay = moment(calendarStart);
  
  while (currentDay.isSameOrBefore(calendarEnd)) {
    days.push({
      date: currentDay.format('YYYY-MM-DD'),
      day: currentDay.date(),
      isCurrentMonth: currentDay.month() === monthStart.month(),
      dayOfWeek: currentDay.format('ddd')
    });
    currentDay.add(1, 'day');
  }
  
  // Group days into weeks
  const weeks = [];
  for (let i = 0; i < days.length; i += 7) {
    weeks.push(days.slice(i, i + 7));
  }
  
  // Helper function to get bookings for a specific date
  const getBookingsForDate = (date) => {
    return bookings.filter(booking => moment(booking.start).format('YYYY-MM-DD') === date);
  };

  const getBookingId = useCallback((booking) => {
    const rawId = booking?._id ?? booking?.id ?? booking?.bookingId ?? booking?.eventId;
    return rawId ? String(rawId) : '';
  }, []);

  const handleBookingNavigate = useCallback((booking) => {
    const bookingId = getBookingId(booking);
    if (!bookingId) return;
    navigate(`/schedule/details/${bookingId}`);
  }, [getBookingId, navigate]);

  return (
    <div className="bg-white shadow-sm rounded-lg border border-gray-200">
      <div className="overflow-x-auto">
        {/* Weekday headers */}
        <div className="grid grid-cols-7 border-b border-gray-200">
          {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day, index) => (
            <div 
              key={index}
              className="px-4 py-3 text-center text-sm font-semibold text-gray-900 bg-gray-50 border-r border-gray-200 last:border-r-0"
            >
              {day}
            </div>
          ))}
        </div>

        {/* Calendar grid */}
        <div className="divide-y divide-gray-200">
          {weeks.map((week, weekIndex) => (
            <div key={weekIndex} className="grid grid-cols-7">
              {week.map((day, dayIndex) => {
                const bookingsForDay = getBookingsForDate(day.date);
                const firstTwo = bookingsForDay.slice(0, 2);
                const moreCount = bookingsForDay.length - 2;
                return (
                  <div
                    key={dayIndex}
                    onClick={() => onDayClick(day.date)}
                    className={`min-h-[100px] p-2 border-r border-gray-200 last:border-r-0 cursor-pointer hover:bg-gray-50 transition-colors ${
                      !day.isCurrentMonth ? 'bg-gray-50' : ''
                    }`}
                  >
                    <div className={`text-sm font-medium mb-1 ${
                      day.isCurrentMonth ? 'text-gray-900' : 'text-gray-400'
                    }`}>
                      {day.day}
                    </div>
                    {bookingsForDay.length > 0 && (
                      <div className="space-y-1">
                        {firstTwo.map((booking, idx) => {
                          const bookingKey = getBookingId(booking) || `${day.date}-${idx}`;
                          return (
                            <div
                              key={bookingKey}
                              className="cursor-pointer"
                              onClick={() => handleBookingNavigate(booking)}
                            >
                              <BookingItemMonth booking={booking} />
                            </div>
                          );
                        })}
                        {moreCount > 0 && (
                          <div className="text-xs text-blue-600 font-semibold mt-1">{moreCount} more</div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default MonthView;
